import { trans } from "@mongez/localization";
import { isEmpty } from "@mongez/supportive-is";
import { Filter, FilterType, productsListAtom } from "core/catalog";
import { createPortal } from "core/shared";
import { useEffect, useState } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "~/design-system/components/ui/accordion";
import { Portal } from "~/modules/shared/components";
import { ApplyFiltersButton } from "./ApplyFiltersButton";
import { BooleanFilter } from "./BooleanFilter";
import { PriceFilter } from "./PriceFilter";
import { ResetFiltersButton } from "./ResetFiltersButton";
import { SelectFilter } from "./SelectFilter";

type FilterProps = {
  filter: Filter;
  activeFilters: string[];
};

const filtersMap: Record<FilterType, React.FC<FilterProps>> = {
  brands: SelectFilter,
  categories: SelectFilter,
  price: PriceFilter,
  inStock: BooleanFilter,
  hasDiscount: BooleanFilter,
};

export const filtersPortal = createPortal("filters");

export function MobileFiltersPortal() {
  const portalOpened = filtersPortal.useOpened();
  const storedFilters = productsListAtom.use("filters");
  const [filters, setFilters] = useState<Filter[]>([]);

  useEffect(() => {
    if (storedFilters && portalOpened) {
      setFilters(storedFilters);
    }
  }, [storedFilters, portalOpened]);

  const defaultValue = filters.find((filter) => !isEmpty(filter.data))?.type;

  return (
    <Portal
      portal={filtersPortal}
      title={trans("filters")}
      classes={{
        header: {
          container: "pb-4",
        },
      }}
    >
      <Accordion
        collapsible
        type="single"
        defaultValue={defaultValue}
        className="flex h-fit! flex-col"
      >
        {Array.isArray(filters) && filters.length > 0
          ? filters.map((filter, index) => {
              const FilterComponent = filtersMap[filter.type as FilterType];

              if (!FilterComponent || isEmpty(filter.data) || !filter.data)
                return null;

              const params = new URLSearchParams();

              const filterActiveFilters = params.getAll(filter.type);

              return (
                <AccordionItem value={filter.type} className="py-4" key={index}>
                  <AccordionTrigger className="cursor-pointer p-0 font-bold uppercase">
                    {trans(filter.label)}
                  </AccordionTrigger>
                  <AccordionContent className="mt-3 flex max-h-44 flex-col gap-4 overflow-y-auto">
                    <FilterComponent
                      activeFilters={filterActiveFilters}
                      filter={filter}
                    />
                  </AccordionContent>
                </AccordionItem>
              );
            })
          : null}
      </Accordion>
      <div className="flex flex-col items-center gap-3 px-4">
        <ApplyFiltersButton onClick={filtersPortal.close} />
        <ResetFiltersButton onClick={filtersPortal.close} />
      </div>
    </Portal>
  );
}
